import React, { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import {
  AlertCircle,
  CheckCircle2,
  Loader2,
  LogIn,
  Trophy,
  User,
  UserPlus,
  Users,
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import entryService, { TeamInvitationInfo } from '../services/entryService';

const ACCENT = '#3fced1';

const inputClass =
  'w-full px-4 py-3 rounded-xl bg-zinc-950 border border-zinc-700 text-white text-sm placeholder-gray-600 focus:outline-none focus:border-[#3fced1] transition [color-scheme:dark]';

const TeamInvitationPage: React.FC = () => {
  const { code } = useParams<{ code: string }>();
  const { user, isAuthenticated, isLoading: authLoading } = useAuth();

  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [team, setTeam] = useState<TeamInvitationInfo | null>(null);

  // Beitritts-Formular
  const [firstname, setFirstname] = useState('');
  const [lastname, setLastname] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [birthday, setBirthday] = useState('');
  const [formError, setFormError] = useState<string | null>(null);
  const [joining, setJoining] = useState(false);
  const [joined, setJoined] = useState(false);

  const load = useCallback(async () => {
    if (!code) return;
    setLoading(true);
    setLoadError(null);
    try {
      const data = await entryService.getTeamByInvitationCode(code);
      if (!data) {
        setLoadError('Einladung konnte nicht geladen werden.');
        return;
      }
      setTeam(data);
    } catch (err: any) {
      const status = err?.response?.status;
      if (status === 404) {
        setLoadError('Dieser Einladungscode ist ungültig oder wurde erneuert.');
      } else {
        setLoadError(entryService.getErrorMessage(err, 'Einladung konnte nicht geladen werden.'));
      }
    } finally {
      setLoading(false);
    }
  }, [code]);

  useEffect(() => {
    load();
  }, [load]);

  // Vorbefüllung aus eingeloggtem User
  useEffect(() => {
    if (!user) return;
    setFirstname((prev) => prev || user.firstname || (user as any)?.personalData?.firstname || '');
    setLastname((prev) => prev || user.lastname || (user as any)?.personalData?.lastname || '');
    setEmail((prev) => prev || user.email || (user as any)?.personalData?.email || '');
    setPhone((prev) => prev || (user as any)?.personalData?.phone || '');
  }, [user]);

  const handleJoin = async () => {
    if (!code) return;
    if (!firstname.trim() || !lastname.trim()) {
      setFormError('Bitte gib Vor- und Nachnamen an.');
      return;
    }
    if (email.trim() && !/^[\w-.]+@([\w-]+\.)+[\w-]{2,}$/.test(email.trim())) {
      setFormError('Ungültige E-Mail-Adresse.');
      return;
    }
    setJoining(true);
    setFormError(null);
    try {
      await entryService.joinTeamByInvitation(code, {
        firstname: firstname.trim(),
        lastname: lastname.trim(),
        email: email.trim() || null,
        phone: phone.trim() || null,
        birthday: entryService.inputValueToBirthday(birthday),
      });
      setJoined(true);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err: any) {
      setFormError(entryService.getErrorMessage(err, 'Beitritt fehlgeschlagen.'));
    } finally {
      setJoining(false);
    }
  };

  const teamFull =
    typeof team?.currentPlayers === 'number' &&
    typeof team?.maxPlayers === 'number' &&
    team.maxPlayers > 0 &&
    team.currentPlayers >= team.maxPlayers;

  // ===================================================================
  // Render
  // ===================================================================

  if (loading || authLoading) {
    return (
      <div className="min-h-screen bg-black flex flex-col items-center justify-center px-6 py-20">
        <div className="h-10 w-10 rounded-full border-2 border-white/20 border-t-white animate-spin" />
        <p className="mt-4 text-sm text-gray-400">Einladung laden…</p>
      </div>
    );
  }

  if (loadError || !team) {
    return (
      <div className="min-h-screen bg-black flex flex-col items-center justify-center px-6 py-20 text-center">
        <Helmet>
          <title>Team-Einladung | DartCloud</title>
          <meta name="robots" content="noindex" />
        </Helmet>
        <div className="h-16 w-16 rounded-full bg-white/5 border border-white/10 flex items-center justify-center mb-4">
          <AlertCircle size={32} className="text-gray-400" />
        </div>
        <h1 className="text-xl font-bold text-white mb-2">Einladung nicht verfügbar</h1>
        <p className="text-sm text-gray-400 mb-6 max-w-md">{loadError || 'Diese Einladung ist nicht mehr gültig.'}</p>
        <Link
          to="/turniere"
          className="px-4 py-2 rounded-lg bg-white text-black font-medium text-sm hover:bg-gray-200 transition"
        >
          Zu den Turnieren
        </Link>
      </div>
    );
  }

  const teamName = team.teamName || 'Team';
  const tournamentName = team.tournamentName || 'Turnier';

  return (
    <div className="min-h-screen bg-black text-white">
      <Helmet>
        <title>{`Team-Einladung – ${teamName} | DartCloud`}</title>
        <meta name="robots" content="noindex" />
      </Helmet>

      <div className="max-w-xl mx-auto px-4 sm:px-6 py-10 sm:py-14">
        {/* Team-Info */}
        <div className="p-6 sm:p-8 rounded-2xl bg-zinc-900 border border-zinc-800 text-center mb-5">
          <div className="h-16 w-16 rounded-full mx-auto flex items-center justify-center mb-4" style={{ backgroundColor: `${ACCENT}22` }}>
            <Users size={30} style={{ color: ACCENT }} />
          </div>
          <p className="text-sm text-gray-400 mb-1">Du wurdest eingeladen, dem Team beizutreten:</p>
          <h1 className="text-2xl sm:text-3xl font-bold mb-2 break-words">{teamName}</h1>
          {team.tournamentSlug ? (
            <Link
              to={`/turnier/${team.tournamentSlug}`}
              className="inline-flex items-center gap-1.5 text-sm font-semibold hover:underline"
              style={{ color: ACCENT }}
            >
              <Trophy size={14} />
              {tournamentName}
            </Link>
          ) : (
            <p className="inline-flex items-center gap-1.5 text-sm font-semibold" style={{ color: ACCENT }}>
              <Trophy size={14} />
              {tournamentName}
            </p>
          )}

          {/* Spieler */}
          <div className="mt-5 pt-5 border-t border-zinc-800">
            <p className="text-xs font-semibold text-gray-400 mb-3">
              Aktuelle Spieler
              {typeof team.currentPlayers === 'number' && (
                <>
                  {' '}
                  ({team.currentPlayers}
                  {typeof team.maxPlayers === 'number' && team.maxPlayers > 0 ? ` / ${team.maxPlayers}` : ''})
                </>
              )}
            </p>
            <div className="flex flex-wrap justify-center gap-1.5">
              {(team.players || []).length === 0 ? (
                <p className="text-xs text-gray-500">Noch keine Spieler im Team.</p>
              ) : (
                (team.players || []).map((p, idx) => (
                  <span
                    key={idx}
                    className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md bg-zinc-800 border border-zinc-700 text-xs text-gray-300"
                  >
                    <User size={11} className="text-gray-500" />
                    {p?.name || `Spieler ${idx + 1}`}
                  </span>
                ))
              )}
            </div>
          </div>
        </div>

        {/* Beitritt */}
        {joined ? (
          <div className="p-6 sm:p-8 rounded-2xl bg-zinc-900 border border-zinc-800 text-center">
            <div className="h-14 w-14 rounded-full mx-auto flex items-center justify-center mb-4 bg-emerald-500/15">
              <CheckCircle2 size={26} className="text-emerald-500" />
            </div>
            <h2 className="text-xl font-bold mb-2">Du bist dem Team beigetreten!</h2>
            <p className="text-sm text-gray-400 mb-6">
              Du bist jetzt Teil von „{teamName}" für {tournamentName}.
            </p>
            <Link
              to="/meine-anmeldungen"
              className="inline-flex items-center justify-center py-3 px-6 rounded-xl font-semibold text-black transition hover:opacity-90"
              style={{ backgroundColor: ACCENT }}
            >
              Meine Anmeldungen
            </Link>
          </div>
        ) : teamFull ? (
          <div className="p-6 sm:p-8 rounded-2xl bg-zinc-900 border border-zinc-800 text-center">
            <AlertCircle size={28} className="mx-auto text-amber-500 mb-3" />
            <h2 className="text-lg font-bold mb-2">Team ist voll</h2>
            <p className="text-sm text-gray-400">Dieses Team hat bereits die maximale Spieleranzahl erreicht.</p>
          </div>
        ) : !isAuthenticated ? (
          <div className="p-6 sm:p-8 rounded-2xl bg-zinc-900 border border-zinc-800 text-center">
            <div className="h-14 w-14 rounded-full mx-auto flex items-center justify-center mb-4" style={{ backgroundColor: `${ACCENT}22` }}>
              <LogIn size={26} style={{ color: ACCENT }} />
            </div>
            <h2 className="text-xl font-bold mb-2">Anmeldung erforderlich</h2>
            <p className="text-sm text-gray-400 mb-6">
              Du musst eingeloggt sein, um dem Team beizutreten.
            </p>
            <Link
              to="/login"
              className="inline-flex items-center justify-center gap-2 py-3 px-6 rounded-xl font-semibold text-black transition hover:opacity-90"
              style={{ backgroundColor: ACCENT }}
            >
              <LogIn size={16} />
              Jetzt einloggen
            </Link>
          </div>
        ) : (
          <div className="p-6 sm:p-8 rounded-2xl bg-zinc-900 border border-zinc-800">
            <h2 className="text-lg font-bold mb-5 flex items-center gap-2">
              <UserPlus size={18} style={{ color: ACCENT }} />
              Deine Daten
            </h2>

            {formError && (
              <div className="mb-4 p-3 rounded-xl border border-red-500/40 bg-red-500/10 flex items-start gap-2">
                <AlertCircle size={16} className="text-red-400 shrink-0 mt-0.5" />
                <p className="text-sm text-red-300">{formError}</p>
              </div>
            )}

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1.5">
                  Vorname<span className="text-red-500 ml-1">*</span>
                </label>
                <input value={firstname} onChange={(e) => setFirstname(e.target.value)} className={inputClass} />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1.5">
                  Nachname<span className="text-red-500 ml-1">*</span>
                </label>
                <input value={lastname} onChange={(e) => setLastname(e.target.value)} className={inputClass} />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1.5">E-Mail</label>
                <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} className={inputClass} />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1.5">Telefon</label>
                <input type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} className={inputClass} />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1.5">Geburtsdatum</label>
                <input type="date" value={birthday} onChange={(e) => setBirthday(e.target.value)} className={inputClass} />
              </div>
            </div>

            <button
              onClick={handleJoin}
              disabled={joining}
              className="mt-6 w-full py-3.5 rounded-xl font-bold text-black transition hover:opacity-90 disabled:opacity-50 flex items-center justify-center gap-2"
              style={{ backgroundColor: ACCENT }}
            >
              {joining ? (
                <>
                  <Loader2 size={18} className="animate-spin" />
                  Beitreten…
                </>
              ) : (
                <>
                  <UserPlus size={18} />
                  Team beitreten
                </>
              )}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default TeamInvitationPage;
